import { createFileRoute, SearchSchemaInput, useNavigate, useRouter } from '@tanstack/react-router'
import backIcon from "@/assets/paytm/back.png"
import { useMutation, useQuery } from '@tanstack/react-query'
import { useStore } from '@/store'
import { useShallow } from 'zustand/shallow'
import { Api } from '@/lib/utils'
import { Order } from '@/lib/interfaces'
import dayjs from 'dayjs'
import Select from '@/components/features/paytm/select'
import { useEffect, useState } from 'react'
import Button from '@/components/features/paytm/button'
import { toast } from 'sonner'

export const Route = createFileRoute('/paytm/help')({
    component: RouteComponent,
    validateSearch: (search: Record<string, unknown> & SearchSchemaInput): {
        forOrder: string | undefined
    } => {
        return {
            forOrder: search.forOrder as string || undefined
        }
    }
})

function RouteComponent() {
    const { forOrder } = Route.useSearch()
    const router = useRouter()
    const navigate = useNavigate()
    const { token, setPaytmTab } = useStore(useShallow(state => ({ token: state.token, setPaytmTab: state.setPaytmTab })))

    const getOrders = useQuery({
        queryKey: ['orders', token],
        queryFn: () => Api<Order[]>('/webui/orders_detailed_v2', "POST", { date: dayjs().format('YYYY-MM-DD') }, { token }),
        staleTime: 60 * 1000,
        refetchInterval: 90 * 1000,
        refetchOnWindowFocus: false
    })

    const [orderId, setOrderId] = useState('')
    const [issue, setIssue] = useState('')
    const [description, setDescription] = useState('')

    useEffect(() => {
        setPaytmTab('Help')
        if (forOrder) {
            setOrderId(forOrder)
        }
    }, [])

    const issuesList = [
        "Delay in delivery",
        "Rider ran away with the item",
        "Order is lost post-pickup by Rider",
        "Order is RTO Disposed by rider",
        "Food Spillage or Item Damaged",
        "MDND - Marked delivered without delivering",
        "Delay in pickup",
        "Marked delivered without picking up items",
        "Physically Delivered but not marked delivered in app",
        "Fake pickup",
        "Rude agent"
    ]

    const filterIssueReasons = (orderStatus: string, reason: string) => {
        switch (reason) {
            case "Delay in delivery":
                return !['Order-delivered', 'Cancelled', 'RTO-Initiated', 'RTO-Disposed', 'RTO-Delivered'].includes(orderStatus)
            case "Rider ran away with the item":
                return orderStatus !== 'Order-delivered'
            case "Order is lost post-pickup by Rider":
                return ["Order-picked-up", "Out-for-delivery", "At-delivery", "RTO-Initiated"].includes(orderStatus)
            case "Order is RTO Disposed by rider":
                return ["RTO-Initiated", "RTO-Disposed"].includes(orderStatus)
            case "Food Spillage or Item Damaged":
                return ["Order-picked-up", "Out-for-delivery", "At-delivery", "Order-delivered", "RTO-Delivered", "RTO-Initiated"].includes(orderStatus)
            case "MDND - Marked delivered without delivering":
                return orderStatus === 'Order-delivered'
            case "Marked delivered without picking up items":
                return orderStatus === 'Order-delivered'
            case "Delay in pickup":
                return ['Created', 'UnFulfilled', 'Pending', 'Searching-for-Agent', 'Agent-assigned'].includes(orderStatus)
            case "Physically Delivered but not marked delivered in app":
                return ["Order-picked-up", "At-delivery"].includes(orderStatus)
            case "Fake pickup":
                return ["Order-picked-up", "Out-for-delivery", "At-delivery"].includes(orderStatus)
            case "Rude agent":
                return ["Agent-assigned", "At-pickup", "Order-picked-up", "Out-for-delivery", "At-delivery", "Order-delivered", "Cancelled", "RTO-Initiated", "RTO-Disposed", "RTO-Delivered"].includes(orderStatus)
        }
    }

    const raiseIssue = useMutation({
        mutationFn: (data: { issueType: string, description: string }) => {
            return Api<{ status: number, message: string }>('/webui/order/issue', "POST", {
                order: {
                    id: orderId,
                    network_id: undefined
                },
                description: {
                    line1: data.issueType,
                    line2: data.description,
                    image: undefined
                },
                refund_amount: undefined,
                route: 'paytm'
            }, { token })
        },
        onError(error) {
            toast.error(error.message ||  "Error raising issue against order")
        },
        onSuccess() {
            toast.success(`Successfully raised issue`)
            setOrderId('')
            setIssue('')
            setDescription('')
        },
    })

    return <div className='w-full'>
        <div className="h-14 flex justify-between px-4 items-center bg-light-blue">
            <div className="flex items-center">
                <img src={backIcon} className="w-5 mr-4" onClick={() => router.history.back()} />
                <p className="font-semibold text-lg">Raise Issue</p>
                <div className="w-16 h-5 ml-10" onClick={() => navigate({ to: '/paytm/profile' })} />
            </div>
        </div>
        <div className="flex flex-col items-center *:my-2 m-4 p-4 border rounded-2xl">
            <Select label="Order" options={(getOrders?.data || []).map(e => ({ label: e.clientOrderId, value: e.orderId }))} value={orderId} onChange={val => {
                setOrderId(val)
                setIssue('')
                setDescription('')
            }} hideSearch size='max' />
            <Select label="Issue" options={[...issuesList].filter(e => filterIssueReasons((getOrders?.data || []).find(e => e.orderId === orderId)?.orderState || '', e)).map(e => ({ label: e, value: e }))}
                value={issue} onChange={val => setIssue(val)} hideSearch size='max' />
            <div className="border w-full rounded-lg p-2 relative border-gray-300">
                <p className="absolute -top-3 left-2 bg-white px-1 text-sm font-medium text-gray-500">Description</p>
                <textarea value={description} onChange={e => setDescription(e.target.value)} className="h-24 resize-none p-1 text-sm outline-none w-full" />
            </div>
            <Button title="Raise Issue" onClick={() => raiseIssue.mutate({ issueType: issue, description })} disabled={!orderId || !issue || !description} loading={raiseIssue.isPending} />
        </div>
    </div>
}
